import { useEffect, useState, useCallback } from "react";
import {
  ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from "recharts";
import { Globe, ClipboardText, Stethoscope } from "@phosphor-icons/react";
import { motion } from "framer-motion";
import api from "../lib/api";
import { fmt, fmtCompact, GREEN } from "../lib/format";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "./ui/select";
import { Button } from "./ui/button";

const CARD_META = {
  ASURANSI: { icon: Globe, color: "#5FA777", tint: "#EAF5EC" },
  "BPJS KESEHATAN": { icon: ClipboardText, color: "#1C4A37", tint: "#E7F2EA" },
  UMUM: { icon: Stethoscope, color: "#3E7C5A", tint: "#EDF6EF" },
};
const STACK = [
  { key: "UMUM", color: "#1C4A37" },
  { key: "ASURANSI", color: "#5FA777" },
  { key: "BPJS KESEHATAN", color: "#A5D983", name: "BPJS KES" },
];

function monthLabel(ym) {
  if (!ym || ym === "all") return "Semua Bulan";
  const [y, m] = ym.split("-");
  const names = ["Jan", "Feb", "Mar", "Apr", "Mei", "Jun", "Jul", "Agu", "Sep", "Okt", "Nov", "Des"];
  return `${names[parseInt(m, 10) - 1]} ${y}`;
}

function shortDoctor(name) {
  return String(name).replace(/^dr\.?\s*/i, "").replace(/^drg\.?\s*/i, "");
}

function ChartTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border border-border shadow-lg rounded-lg px-3 py-2 text-xs space-y-0.5">
      <p className="text-muted-foreground mb-1 font-semibold">{label}</p>
      {payload.map((p) => (
        <p key={p.dataKey} className="flex items-center justify-between gap-4">
          <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full" style={{ background: p.color }} />{p.name}</span>
          <span className="font-display font-bold tabular text-foreground">{fmt(p.value)}</span>
        </p>
      ))}
    </div>
  );
}

export default function PenjaminPanel({ dsId, onAvailable, filters = [], doctorDim, measure, penjaminField, monthColumn, month, setMonth, dataMonths = [] }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [topDoctors, setTopDoctors] = useState({});
  const [pendingMonth, setPendingMonth] = useState(month || "all");

  useEffect(() => { setPendingMonth(month || "all"); }, [month]);

  const load = useCallback(async (m) => {
    setLoading(true);
    try {
      const { data: res } = await api.post(`/datasources/${dsId}/penjamin`, {
        month: m && m !== "all" ? m : null,
        filters,
      });
      // On first load (no month yet): pick default month and defer painting to avoid
      // flashing all-months totals; the month effect will fetch the scoped data.
      if (m == null && res.available && res.realized_months?.length) {
        onAvailable?.(true);
        setMonth(res.realized_months[res.realized_months.length - 1]);
        return; // keep loading; scoped fetch will populate + clear loading
      }
      setData(res);
      onAvailable?.(!!res.available);
      if (m == null && res.available) setMonth("all");
      setLoading(false);
    } catch {
      onAvailable?.(false);
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dsId, onAvailable, setMonth, JSON.stringify(filters)]);

  useEffect(() => { load(month); }, [load, month]);

  const cards = data?.cards || [];
  const names = cards.map((c) => c.name).join("|");

  // fetch top-5 doctors per penjamin from main sheet (respects month filter)
  useEffect(() => {
    if (!doctorDim || !measure || !penjaminField || !cards.length) return;
    let cancelled = false;
    (async () => {
      const entries = await Promise.all(cards.map(async (c) => {
        try {
          const { data: res } = await api.post(`/datasources/${dsId}/query`, {
            dimension: doctorDim, measure, aggregation: "sum",
            filters: [...filters, { column: penjaminField, values: [c.name] }], limit: 5,
            month, month_column: monthColumn,
          });
          return [c.name, res.data || []];
        } catch {
          return [c.name, []];
        }
      }));
      if (!cancelled) setTopDoctors(Object.fromEntries(entries));
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dsId, doctorDim, measure, penjaminField, monthColumn, month, names, JSON.stringify(filters)]);

  if (loading && !data) {
    return <div className="text-xs text-muted-foreground animate-pulse mb-5" data-testid="penjamin-loading">Memuat ringkasan penjamin…</div>;
  }
  if (!data || !data.available) return null;

  return (
    <div className="mb-5" data-testid="penjamin-panel">
      <div className="flex items-center justify-between gap-4 mb-3 flex-wrap">
        <h2 className="font-display font-bold text-lg tracking-tight">Ringkasan Penjamin</h2>
        <div className="flex items-center gap-2">
          <Select value={pendingMonth} onValueChange={setPendingMonth}>
            <SelectTrigger className="h-9 w-44 text-sm rounded-xl bg-card" data-testid="penjamin-month-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="max-h-72">
              <SelectItem value="all">Semua Bulan</SelectItem>
              {(data.realized_months || []).slice().reverse().map((m) => (
                <SelectItem key={m} value={m}>
                  <span className="flex items-center gap-2">
                    {monthLabel(m)}
                    {dataMonths.includes(m) && (
                      <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: "#5FA777" }}
                        title="Ada data kunjungan" data-testid={`month-has-kunjungan-${m}`} />
                    )}
                  </span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button size="sm" className="h-9 rounded-xl px-4" data-testid="penjamin-apply-month"
            onClick={() => setMonth(pendingMonth)} disabled={pendingMonth === (month || "all")}>
            Terapkan
          </Button>
        </div>
      </div>

      {dataMonths.length > 0 && (
        <p className="text-[11px] text-muted-foreground mb-3 -mt-1 flex items-center gap-1.5" data-testid="kunjungan-month-legend">
          <span className="w-1.5 h-1.5 rounded-full" style={{ background: "#5FA777" }} /> Titik hijau menandai bulan dengan data kunjungan pasien
        </p>
      )}

      {/* Cards with top-5 doctors */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-3">
        {cards.map((c, i) => {
          const meta = CARD_META[c.name] || { icon: Globe, color: "#5FA777", tint: "#EAF5EC" };
          const Icon = meta.icon;
          const docs = topDoctors[c.name] || [];
          return (
            <motion.div key={c.name} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.05 }}
              className="rounded-2xl bg-card p-4 flex flex-col shadow-sm ring-1 ring-black/[0.03]"
              data-testid={`penjamin-card-${c.name.replace(/\s+/g, "-").toLowerCase()}`}>
              <div className="flex items-start justify-between">
                <span className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground font-semibold">{c.name}</span>
                <span className="w-8 h-8 rounded-xl flex items-center justify-center" style={{ background: meta.tint }}>
                  <Icon size={16} weight="fill" style={{ color: meta.color }} />
                </span>
              </div>
              <div className="mt-2 flex items-end justify-between gap-2">
                <div className="font-display font-extrabold text-lg tracking-tight tabular text-foreground leading-tight" title={fmt(c.value)}>
                  {fmt(c.value)}
                </div>
                <span className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold shrink-0"
                  style={{ background: meta.tint, color: meta.color }}>{c.pct.toFixed(2)}%</span>
              </div>

              {/* Target + achievement */}
              <div className="mt-2.5">
                <div className="flex items-center justify-between text-[11px] mb-1">
                  <span className="text-muted-foreground">Target: <span className="tabular font-semibold text-foreground">{fmt(c.target)}</span></span>
                  <span className="tabular font-bold" style={{ color: (c.achievement || 0) >= 100 ? "#1C4A37" : "#C2557A" }}
                    data-testid={`penjamin-achievement-${c.name.replace(/\s+/g, "-").toLowerCase()}`}>
                    {(c.achievement || 0).toFixed(1)}%
                  </span>
                </div>
                <div className="h-1.5 rounded-full bg-secondary overflow-hidden">
                  <div className="h-full rounded-full" style={{ width: `${Math.min(100, c.achievement || 0)}%`, background: meta.color }} />
                </div>
              </div>

              {doctorDim && (
                <div className="mt-3 pt-3 border-t border-border/70">
                  <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold mb-2">Top 5 Dokter</p>
                  <div className="space-y-1.5">
                    {docs.length === 0 ? (
                      <p className="text-xs text-muted-foreground">—</p>
                    ) : docs.map((d, idx) => (
                      <div key={idx} className="flex items-center justify-between gap-2 text-xs" data-testid={`penjamin-doctor-${c.name.replace(/\s+/g, "-").toLowerCase()}-${idx}`}>
                        <span className="truncate flex items-center gap-1.5 min-w-0">
                          <span className="w-3.5 text-muted-foreground tabular text-[10px] shrink-0">{idx + 1}</span>
                          <span className="truncate" title={d.name}>{shortDoctor(d.name)}</span>
                        </span>
                        <span className="tabular font-semibold shrink-0" style={{ color: meta.color }} title={fmt(d.value)}>{fmtCompact(d.value)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </motion.div>
          );
        })}
      </div>

      {/* Stacked daily chart */}
      <div className="rounded-2xl bg-card p-4 shadow-sm ring-1 ring-black/[0.03]" data-testid="penjamin-chart">
        <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
          <h3 className="font-display font-bold text-base">Penjualan per Hari & Penjamin</h3>
          <span className="text-xs text-muted-foreground">
            Realisasi <span className="font-semibold text-foreground tabular">{fmtCompact(data.total_realisasi)}</span>
            {" · "}Target <span className="font-semibold text-foreground tabular">{fmtCompact(data.total_target)}</span>
          </span>
        </div>
        {(!data.daily || data.daily.length === 0) ? (
          <div className="h-[280px] flex items-center justify-center text-xs text-muted-foreground">Tidak ada data harian</div>
        ) : (
          <ResponsiveContainer width="100%" height={280}>
            <ComposedChart data={data.daily} margin={{ top: 8, right: 6, left: 0, bottom: 4 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={GREEN.grid} vertical={false} />
              <XAxis dataKey="date" stroke={GREEN.axis} fontSize={9} tickLine={false} interval="preserveStartEnd" />
              <YAxis stroke={GREEN.axis} fontSize={9} tickLine={false} axisLine={false} tickFormatter={fmtCompact} />
              <Tooltip content={<ChartTooltip />} cursor={{ fill: "rgba(28,74,55,0.05)" }} />
              <Legend wrapperStyle={{ fontSize: 10, color: "#6B7B72" }} iconType="circle" />
              {STACK.map((s, idx) => (
                <Bar key={s.key} dataKey={s.key} name={s.name || s.key} stackId="a" fill={s.color}
                  maxBarSize={24} radius={idx === STACK.length - 1 ? [5, 5, 0, 0] : [0, 0, 0, 0]} />
              ))}
              <Line type="monotone" dataKey="REALISASI" name="REALISASI" stroke="#C2557A" strokeWidth={2} dot={false} />
              <Line type="monotone" dataKey="TARGET" name="TARGET" stroke="#94A3B8" strokeWidth={2} strokeDasharray="6 4" dot={false} />
            </ComposedChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
}
